import React, { useState, useEffect } from 'react';
import useCalendar from '../hooks/useCalendar';
import { useCalendarNotifications } from '../hooks/useCalendarNotifications';
import { useTheme } from '../contexts/ThemeContext';
import { useToast } from '../hooks/useToast';
import calendarService from '../services/calendarService';

import CalendarHeader from '../components/Calendar/CalendarHeader';
import WeekView from '../components/Calendar/WeekView';
import MonthView from '../components/Calendar/MonthView';
import YearView from '../components/Calendar/YearView';
import QuickEventModal from '../components/Calendar/QuickCreateModal';
import MultiDayEventModal from '../components/Calendar/MultiDayEventModal';
import DateSelectionPopup from '../components/Calendar/DateSelectionPopup';
import { FaDownload, FaPaperPlane } from 'react-icons/fa';

const CalendarioPage = () => {
  const { theme } = useTheme();
  const { showSuccess, showError, showInfo } = useToast();
  const showToast = (message, type = 'info') => {
    if (type === 'success') showSuccess(message);
    else if (type === 'error') showError(message);
    else showInfo(message);
  };
  
  // Calendar hooks
  const {
    currentView,
    changeView,
    currentDate,
    eventi,
    weekDates,
    eventiOggi,
    isLoading: loading,
    setIsLoading,
    error,
    setDate: handleDateChange,
    navigateDate: handleWeekNavigate,
    creaEvento: handleCreateEvent,
    modificaEvento: handleUpdateEvent,
    eliminaEvento: handleDeleteEvent,
    refreshEvents
  } = useCalendar();

  // Notifications
  const { unreadCount } = useCalendarNotifications();

  // UI State
  const [showQuickCreate, setShowQuickCreate] = useState(false);
  const [showMultiDayModal, setShowMultiDayModal] = useState(false);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState({ date: null, hour: 9 });
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedDates, setSelectedDates] = useState([]);
  const [showDateSelectionPopup, setShowDateSelectionPopup] = useState(false);
  const [popupPosition, setPopupPosition] = useState({ x: 0, y: 0 });
  
  // ✅ NEW: Stati per filtri tipi evento
  const [eventTypes, setEventTypes] = useState([]);
  const [visibleEventTypes, setVisibleEventTypes] = useState(new Set());
  const [isLoadingEventTypes, setIsLoadingEventTypes] = useState(false);

  // Timbrature download state
  const [showTimbraturePopup, setShowTimbraturePopup] = useState(false);
  const [timbratureMese, setTimbratureMese] = useState(null);
  const [timbratureAnno, setTimbratureAnno] = useState(null);
  const [isDownloadingTimbrature, setIsDownloadingTimbrature] = useState(false);
  const [isSendingTimbrature, setIsSendingTimbrature] = useState(false);

  // ✅ NEW: Carica tipi evento e inizializza filtri
  useEffect(() => {
    const loadEventTypes = async () => {
      setIsLoadingEventTypes(true);
      try {
        const types = await calendarService.getEventTypes();
        setEventTypes(types);
        
        // Preseleziona tutti i tipi come visibili (default)
        const allTypeIds = new Set(types.map(type => type.id));
        setVisibleEventTypes(allTypeIds);
        
      } catch (error) {
        console.error('Errore caricamento tipi evento per filtri:', error);
      } finally {
        setIsLoadingEventTypes(false);
      }
    };
    
    loadEventTypes();
  }, []);
  
  // ✅ NEW: Handler per toggle visibilità tipo evento
  const handleEventTypeToggle = (typeId) => {
    setVisibleEventTypes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(typeId)) {
        newSet.delete(typeId);
      } else {
        newSet.add(typeId);
      }
      return newSet;
    });
  };
  
  // ✅ NEW: Funzione per filtrare eventi in base ai tipi visibili
  const getFilteredEvents = () => {
    if (currentView !== 'month') {
      return eventi; // Filtro solo nella vista mensile
    }
    
    return eventi.filter(evento => {
      const tipoEventoId = evento.CAL_Id_TipiEventi;
      return visibleEventTypes.has(tipoEventoId);
    });
  };

  // ✅ NEW: Funzione per convertire Date in formato MySQL
  const toMySQLDateTime = (date) => {
    if (!date) return null;
    
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const hours = String(d.getHours()).padStart(2, '0');
    const minutes = String(d.getMinutes()).padStart(2, '0');
    const seconds = String(d.getSeconds()).padStart(2, '0');
    
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  };

  // ✅ FIX: Wrapper per handleWeekNavigate con direzione
  const handleWeekNavigation = (direction) => {
    const directionValue = direction === 'next' ? 1 : -1;
    handleWeekNavigate(directionValue);
  };

  // ✅ DEBUG: Wrapper per handleWeekNavigate con logging
  const handleWeekNavigateWithDebug = (direction) => {

    console.log('🔄 Current date before:', currentDate);
    
    try {
      handleWeekNavigate(direction);
      console.log('🔄 handleWeekNavigate called successfully');
    } catch (error) {
      console.error('❌ Error in handleWeekNavigate:', error);
    }
  };

  // Event handlers
  const handleEventSelect = (evento) => {
    setSelectedEvent(evento);
    setShowQuickCreate(true);
  };

  // ✅ FIX: Update handleTimeSlotClick to accept eventDateTime or separate date/hour
  const handleTimeSlotClick = (dateOrEventDateTime, hour) => {
    if (dateOrEventDateTime instanceof Date && hour === undefined) {
      // New format: single Date object (from WeekView)
      setSelectedTimeSlot({ 
        date: dateOrEventDateTime, 
        hour: dateOrEventDateTime.getHours() 
      });
    } else {
      // Legacy format: separate date and hour (from other components)
      setSelectedTimeSlot({ date: dateOrEventDateTime, hour });
    }
    setSelectedEvent(null);
    setShowQuickCreate(true);
  };

  // ✅ FIX: Aggiungi funzione mancante per selezione range di date
  const handleDateRangeSelect = (dates) => {
    console.log('📅 Range di date selezionato:', dates);
    setSelectedDates(dates);
  };

  // ✅ FIX: Aggiungi funzione mancante per creare evento multi-giorno
  const handleMultiDayCreate = () => {
    console.log('📅 Apro modal per evento multi-giorno con date:', selectedDates);
    if (selectedDates.length > 0) {
      setShowMultiDayModal(true);
    } else {
      showToast('Seleziona almeno una data per creare un evento multi-giorno', 'warning');
    }
  };

  // Handle quick event save with improved error handling and notifications
  const handleQuickEventSave = async (eventData) => {
    setIsLoading(true);
    try {
      console.log('📝 Salvataggio evento:', { isEditing: !!selectedEvent, eventData });
      
      if (selectedEvent) {
        // Modifica evento esistente
        console.log('✏️ Aggiornamento evento ID:', selectedEvent.CAL_Id || selectedEvent.CAL_Id_CalendarioEventi);
        await handleUpdateEvent(selectedEvent.CAL_Id || selectedEvent.CAL_Id_CalendarioEventi, eventData);
      } else {
        // Nuovo evento
        console.log('➕ Creazione nuovo evento');
        await handleCreateEvent(eventData);
      }

      console.log('✅ Salvataggio completato, chiudo modal');
      setShowQuickCreate(false);
      setSelectedEvent(null);
      
    } catch (error) {
      console.error('❌ Errore salvataggio evento:', error);
      console.log('⚠️ Errore gestito dall\'hook, non mostro toast aggiuntivo');
    } finally {
      setIsLoading(false);
    }
  };

  // 🆕 Multi-day event handlers
  const handleDateSelect = (dates) => {
    console.log('📅 Date selezionate per evento multi-giorno:', dates);
    setSelectedDates(dates);
    // ✅ RIMOSSO: setShowMultiDayModal(true); - Non aprire automaticamente il modal
  };

  const handleMultiDayEventSave = async (eventData) => {
    setIsLoading(true);
    try {
      console.log('📅 Creazione eventi multi-giorno:', eventData);
      
      for (const event of eventData.events) {
        await handleCreateEvent(event);
      }
      
      console.log(`✅ ${eventData.events.length} eventi multi-giorno creati con successo`);
      showToast(`Eventi multi-giorno creati con successo! (${eventData.events.length} giorni)`, 'success');
      
      setShowMultiDayModal(false);
      setSelectedDates([]);
      
    } catch (error) {
      console.error('❌ Errore creazione eventi multi-giorno:', error);
      showToast(`Errore nella creazione degli eventi multi-giorno: ${error.message}`, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  // 🆕 Year view handlers
  const handleMonthSelect = (monthIndex) => {
    const newDate = new Date(currentDate.getFullYear(), monthIndex, 1);
    handleDateChange(newDate);
    changeView('month');
  };

  const handleQuickRangeCreate = (eventType = 'FERIE') => {
    const today = new Date();
    const nextWeek = new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000);
    
    setSelectedDates([today, nextWeek]);
    setShowMultiDayModal(true);
  };

  // ✅ FIXED: Gestore per spostamento eventi con formato date corretto
  const handleEventMove = async (evento, moveData) => {
    const { newStartDate, newEndDate, originalStartDate } = moveData;
    
    console.log('🎯 Spostamento evento:', {
      id: evento.CAL_Id_CalendarioEventi,
      titolo: evento.CAL_Titolo,
      da: originalStartDate.toLocaleString('it-IT'),
      a: newStartDate.toLocaleString('it-IT')
    });

    try {
      setIsLoading(true);

      // ✅ FIX: Prepara i dati con formato MySQL corretto
      const updatedEventData = {
        ...evento,
        CAL_DataOraInizio: toMySQLDateTime(newStartDate), // ✅ Formato MySQL
        CAL_DataOraFine: newEndDate ? toMySQLDateTime(newEndDate) : evento.CAL_DataOraFine // ✅ Formato MySQL
      };

      console.log('🔄 Dati evento aggiornati:', {
        original: {
          start: evento.CAL_DataOraInizio,
          end: evento.CAL_DataOraFine
        },
        updated: {
          start: updatedEventData.CAL_DataOraInizio,
          end: updatedEventData.CAL_DataOraFine
        }
      });

      // Aggiorna l'evento
      await handleUpdateEvent(evento.CAL_Id_CalendarioEventi, updatedEventData);
      
      // ✅ FIX: showToast è ora disponibile nel scope corretto
      if (showToast) {
        showToast(
          `Evento "${evento.CAL_Titolo}" spostato con successo`, 
          'success'
        );
      } else {
        console.log('✅ Evento spostato con successo (showToast non disponibile)');
      }

    } catch (error) {
      console.error('❌ Errore spostamento evento:', error);
      
      // ✅ FIX: Gestione errore con fallback
      if (showToast) {
        showToast(
          `Errore nello spostamento dell'evento: ${error.message}`, 
          'error'
        );
      } else {
        console.error('❌ Errore spostamento (showToast non disponibile):', error.message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // ✅ NEW: Handler per rimozione eventi multipli
  const handleRemoveEventsFromDates = async (dates) => {
    console.log('🗑️ Rimozione eventi dalle date:', dates);
    
    try {
      setIsLoading(true);
      
      // Trova tutti gli eventi nelle date selezionate
      const eventsToRemove = eventi.filter(evento => {
        const eventDate = new Date(evento.CAL_DataOraInizio);
        return dates.some(date => 
          date.toDateString() === eventDate.toDateString()
        );
      });

      console.log(`🗑️ Trovati ${eventsToRemove.length} eventi da rimuovere`);

      if (eventsToRemove.length === 0) {
        if (showToast) {
          showToast('Nessun evento trovato nelle date selezionate', 'info');
        }
        return;
      }

      // ✅ STRATEGIA: Disabilita temporaneamente le notifiche automatiche dell'hook
      let successCount = 0;
      let errorCount = 0;

      for (const evento of eventsToRemove) {
        try {
          // ✅ Chiama handleDeleteEvent senza aspettare il refresh automatico
          await handleDeleteEvent(evento.CAL_Id_CalendarioEventi);
          successCount++;
          console.log(`✅ Rimosso evento: ${evento.CAL_Titolo}`);
        } catch (error) {
          errorCount++;
          console.error(`❌ Errore rimozione evento ${evento.CAL_Titolo}:`, error);
        }
      }

      // ✅ UNA SOLA NOTIFICA finale
      if (showToast) {
        if (errorCount === 0) {
          showToast(
            `✅ ${successCount} eventi rimossi con successo`,
            'success'
          );
        } else if (successCount > 0) {
          showToast(
            `⚠️ ${successCount} eventi rimossi, ${errorCount} errori`,
            'warning'
          );
        } else {
          showToast(
            `❌ Errore nella rimozione di tutti gli eventi`,
            'error'
          );
        }
      }

      // ✅ REFRESH una sola volta alla fine
      if (successCount > 0) {
        await refreshEvents();
      }

      // Reset selezione
      setSelectedDates([]);

    } catch (error) {
      console.error('❌ Errore generale rimozione eventi:', error);
      if (showToast) {
        showToast(
          `Errore nella rimozione degli eventi: ${error.message}`,
          'error'
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  // ✅ NEW: Handler per aggiunta eventi giornalieri
  const handleAddAllDayEvents = (dates) => {
    console.log('📅 Aggiunta eventi giornalieri alle date:', dates);
    
    // ✅ BATCH: Seleziona tutte le date insieme invece di chiamate multiple
    setSelectedDates(dates);
    setShowMultiDayModal(true);
    
    // ✅ FEEDBACK all'utente
    if (showToast) {
      showToast(
        `📅 Modal aperto per creare eventi in ${dates.length} giorni`,
        'info'
      );
    }
  };

  // ✅ NEW: Handler per apertura popup selezione
  const handleDateSelectionAction = (dates, event) => {
    console.log('📅 Apertura popup per date:', dates);
    
    // ✅ MIGLIORATO: Calcolo posizione più accurato
    let x, y;
    
    if (event && event.getBoundingClientRect) {
      const rect = event.getBoundingClientRect();
      x = rect.left + rect.width / 2;
      y = rect.bottom + 10;
    } else if (event && event.target) {
      const rect = event.target.getBoundingClientRect();
      x = rect.left + rect.width / 2;
      y = rect.bottom + 10;
    } else if (event && event.clientX && event.clientY) {
      x = event.clientX;
      y = event.clientY + 10;
    } else {
      // ✅ FALLBACK: Centro dello schermo
      x = window.innerWidth / 2;
      y = window.innerHeight / 2;
    }
    
    // ✅ VINCOLI: Assicurati che il popup rimanga dentro lo schermo
    const popupWidth = 300; // Larghezza stimata del popup
    const popupHeight = 200; // Altezza stimata del popup
    
    // Aggiusta X se va fuori a destra
    if (x + popupWidth / 2 > window.innerWidth) {
      x = window.innerWidth - popupWidth / 2 - 20;
    }
    
    // Aggiusta X se va fuori a sinistra
    if (x - popupWidth / 2 < 0) {
      x = popupWidth / 2 + 20;
    }
    
    // Aggiusta Y se va fuori in basso
    if (y + popupHeight > window.innerHeight) {
      y = y - popupHeight - 20; // Mostra sopra invece che sotto
    }
    
    // Aggiusta Y se va fuori in alto
    if (y < 0) {
      y = 20;
    }
    
    console.log('📍 Posizione popup:', { x, y, windowSize: { width: window.innerWidth, height: window.innerHeight } });
    
    setPopupPosition({ x, y });
    setSelectedDates(dates);
    setShowDateSelectionPopup(true);
  };

  // ✅ FIX: Handler separato per deselezionare senza aprire modal
  const handleDeselectDates = () => {
    console.log('🚫 Deselezionando date...');
    setSelectedDates([]);
    setShowDateSelectionPopup(false); // ✅ Chiudi anche il popup se aperto
  };

  // Calcola eventi nelle date selezionate
  const getEventsInSelectedDates = () => {
    return eventi.filter(evento => {
      const eventDate = new Date(evento.CAL_DataOraInizio);
      return selectedDates.some(date => 
        date.toDateString() === eventDate.toDateString()
      );
    });
  };

  // Timbrature download handler
  const handleOpenTimbraturePopup = () => {
    setTimbratureAnno(currentDate.getFullYear());
    setTimbratureMese(currentDate.getMonth() + 1);
    setShowTimbraturePopup(true);
  };

  const handleDownloadTimbrature = async () => {
    if (!timbratureAnno || !timbratureMese) return;
    setIsDownloadingTimbrature(true);
    try {
      await calendarService.downloadTimbrature(timbratureAnno, timbratureMese);
      showToast('File timbrature scaricato con successo', 'success');
      setShowTimbraturePopup(false);
    } catch (error) {
      console.error('Errore download timbrature:', error);
      showToast('Errore nel download delle timbrature: ' + (error.response?.data?.message || error.message), 'error');
    } finally {
      setIsDownloadingTimbrature(false);
    }
  };

  const handleSpedisciTimbrature = async () => {
    if (!timbratureAnno || !timbratureMese) return;
    setIsSendingTimbrature(true);
    try {
      const result = await calendarService.spedisciTimbrature(timbratureAnno, timbratureMese);
      showToast(`Timbrature spedite con successo! (${result.data?.statistiche?.totaleRighe || 0} righe)`, 'success');
      setShowTimbraturePopup(false);
    } catch (error) {
      console.error('Errore spedizione timbrature:', error);
      showToast('Errore nella spedizione delle timbrature: ' + (error.response?.data?.message || error.message), 'error');
    } finally {
      setIsSendingTimbrature(false);
    }
  };

  // View renderer
  const renderCurrentView = () => {
    const commonProps = {
      eventi: getFilteredEvents(), // ✅ Usa eventi filtrati per tipo
      onEventSelect: handleEventSelect,
      onTimeSlotClick: handleTimeSlotClick,
      onEventMove: handleEventMove,
    };

    switch (currentView) {
      case 'week':
        return (
          <WeekView
            {...commonProps}
            currentWeek={weekDates || []}
            selectedDate={currentDate}
            onDateSelect={handleDateChange}
            onEventSelect={handleEventSelect}
            onQuickCreate={(eventDateTime) => {
              if (eventDateTime) {
                setSelectedTimeSlot({ 
                  date: eventDateTime, 
                  hour: eventDateTime.getHours() 
                });
              } else {
                setSelectedTimeSlot({ date: null, hour: 9 });
              }
              setSelectedEvent(null);
              setShowQuickCreate(true);
            }}
            navigateDate={handleWeekNavigateWithDebug} // ✅ DEBUG: Usa versione con logging
          />
        );

      case 'month':
        return (
          <MonthView
            {...commonProps}
            currentDate={currentDate}
            selectedDates={selectedDates}
            onDateSelect={handleDateSelect}
            onDateRangeSelect={handleDateRangeSelect}
            onMultiDayCreate={handleMultiDayCreate}
            onDateSelectionAction={handleDateSelectionAction}
          />
        );

      case 'year':
        return (
          <YearView
            {...commonProps}
            currentYear={currentDate.getFullYear()}
            onMonthSelect={handleMonthSelect}
            onQuickRangeCreate={handleQuickRangeCreate}
          />
        );

      case 'today':
      default:
        return (
          <WeekView
            {...commonProps}
            currentWeek={weekDates || []}
            selectedDate={currentDate}
            onDateSelect={handleDateChange}
            navigateDate={handleWeekNavigate} // ✅ CRITICAL: Anche per today view
          />
        );
    }
  };

  // ✅ FIX: useEffect per chiudere modal quando cambia vista
  React.useEffect(() => {
    setShowQuickCreate(false);
    setSelectedEvent(null);
    setShowMultiDayModal(false);
    setSelectedDates([]);
  }, [currentView]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
        <p className="text-red-800 dark:text-red-200">
           Errore caricamento calendario: {error}
        </p>
      </div>
    );
  }

  return (
    <div className={`min-h-screen p-6 transition-colors ${
      theme === 'dark' 
        ? 'bg-gradient-to-br from-gray-900 via-gray-800 to-indigo-900'
        : 'bg-gradient-to-br from-slate-50 via-blue-50 to-emerald-50'
    }`}>
      <div className="max-w-full mx-auto space-y-6">
        {/* Header */}
        <CalendarHeader 
          selectedDate={currentDate}
          currentView={currentView}
          onDateChange={handleDateChange}
          onViewChange={changeView} 
          onWeekNavigate={handleWeekNavigation} 
          onQuickCreate={() => {
            setSelectedTimeSlot({ date: null, hour: 9 });
            setShowQuickCreate(true);
          }}
          onNotificationsToggle={() => {}} 
          unreadCount={unreadCount}
          eventiOggi={eventiOggi?.length || 0}
          eventTypes={eventTypes}
          visibleEventTypes={visibleEventTypes}
          onEventTypeToggle={handleEventTypeToggle}
          isLoadingEventTypes={isLoadingEventTypes}
        />

        {/* Selected dates info (for multi-selection) */}
        {selectedDates.length > 0 && currentView === 'month' && (
          <div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-lg p-4">
            <div className="flex justify-between items-center">
              <div>
                <h3 className="font-medium text-emerald-800 dark:text-emerald-200">
                  {selectedDates.length} giorni selezionati
                </h3>
                <p className="text-sm text-emerald-600 dark:text-emerald-400">
                  {selectedDates.length === 1 
                    ? selectedDates[0].toLocaleDateString('it-IT')
                    : `${selectedDates[0].toLocaleDateString('it-IT')} - ${selectedDates[selectedDates.length - 1].toLocaleDateString('it-IT')}`
                  }
                </p>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => handleMultiDayCreate()}
                  className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
                >
                  Crea Evento
                </button>
                <button
                  onClick={handleDeselectDates} // ✅ FIX: Usa la funzione corretta
                  className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                >
                  Deseleziona
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Main Calendar View */}
        <div className="w-full">
          {renderCurrentView()}
        </div>

        {/* Timbrature Download Button - visibile solo in vista mensile */}
        {currentView === 'month' && (
          <div className="flex justify-end">
            <button
              onClick={handleOpenTimbraturePopup}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors shadow-sm ${
                theme === 'dark'
                  ? 'bg-indigo-600 hover:bg-indigo-700 text-white'
                  : 'bg-emerald-600 hover:bg-emerald-700 text-white'
              }`}
            >
              <FaDownload className="w-4 h-4" />
              Scarica Timbrature
            </button>
          </div>
        )}
      </div>

      {/* Timbrature Popup */}
      {showTimbraturePopup && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowTimbraturePopup(false)}>
          <div
            className={`rounded-xl shadow-2xl p-6 w-96 ${theme === 'dark' ? 'bg-gray-800 text-white' : 'bg-white text-gray-900'}`}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-bold mb-4">Scarica File Timbrature</h3>
            <div className="flex gap-3 mb-4">
              <div className="flex-1">
                <label className={`block text-sm font-medium mb-1 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>Mese</label>
                <select
                  value={timbratureMese}
                  onChange={(e) => setTimbratureMese(parseInt(e.target.value))}
                  className={`w-full rounded-lg border px-3 py-2 ${
                    theme === 'dark' ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                  }`}
                >
                  {['Gennaio','Febbraio','Marzo','Aprile','Maggio','Giugno',
                    'Luglio','Agosto','Settembre','Ottobre','Novembre','Dicembre'].map((nome, i) => (
                    <option key={i} value={i + 1}>{nome}</option>
                  ))}
                </select>
              </div>
              <div className="w-28">
                <label className={`block text-sm font-medium mb-1 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>Anno</label>
                <input
                  type="number"
                  value={timbratureAnno}
                  onChange={(e) => setTimbratureAnno(parseInt(e.target.value))}
                  min={2020}
                  max={2099}
                  className={`w-full rounded-lg border px-3 py-2 ${
                    theme === 'dark' ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                  }`}
                />
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowTimbraturePopup(false)}
                className={`px-4 py-2 rounded-lg transition-colors ${
                  theme === 'dark' ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                }`}
              >
                Annulla
              </button>
              <button
                onClick={handleDownloadTimbrature}
                disabled={isDownloadingTimbrature || isSendingTimbrature}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                  isDownloadingTimbrature || isSendingTimbrature ? 'opacity-50 cursor-not-allowed' : ''
                } ${theme === 'dark' ? 'bg-indigo-600 hover:bg-indigo-700 text-white' : 'bg-emerald-600 hover:bg-emerald-700 text-white'}`}
              >
                <FaDownload className="w-4 h-4" />
                {isDownloadingTimbrature ? 'Download...' : 'Scarica'}
              </button>
              <button
                onClick={handleSpedisciTimbrature}
                disabled={isDownloadingTimbrature || isSendingTimbrature}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                  isDownloadingTimbrature || isSendingTimbrature ? 'opacity-50 cursor-not-allowed' : ''
                } ${theme === 'dark' ? 'bg-blue-600 hover:bg-blue-700 text-white' : 'bg-blue-600 hover:bg-blue-700 text-white'}`}
              >
                <FaPaperPlane className="w-4 h-4" />
                {isSendingTimbrature ? 'Invio...' : 'Spedisci'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modals */}
      {showQuickCreate && (
        <QuickEventModal
          isOpen={showQuickCreate}
          evento={selectedEvent}
          selectedDate={selectedTimeSlot.date}
          selectedHour={selectedTimeSlot.hour}
          selectedTimeSlot={selectedTimeSlot}
          isEditing={!!selectedEvent}
          onSave={handleQuickEventSave}
          onDelete={handleDeleteEvent}  // ✅ ADD: Missing onDelete prop
          onClose={() => {
            setShowQuickCreate(false);
            setSelectedEvent(null);
          }}
        />
      )}

      {showMultiDayModal && (
        <MultiDayEventModal
          isOpen={showMultiDayModal}
          selectedDates={selectedDates}
          onSave={handleMultiDayEventSave}
          onCancel={() => {
            setShowMultiDayModal(false);
            setSelectedDates([]);
          }}
        />
      )}

      {/* ✅ NEW: Date Selection Popup */}
      <DateSelectionPopup
        isOpen={showDateSelectionPopup}
        selectedDates={selectedDates}
        position={popupPosition}
        eventsCount={getEventsInSelectedDates().length}
        onClose={() => setShowDateSelectionPopup(false)}
        onRemoveEvents={handleRemoveEventsFromDates} // ✅ NOW DEFINED
        onAddAllDayEvents={handleAddAllDayEvents} // ✅ NOW DEFINED
      />
    </div>
  );
};

export default CalendarioPage;